import { useState } from "react";
import FaiBox from "MuiComponents/FaiBox";
import FaiTypography from "MuiComponents/FaiTypography";
import theme from "assets/theme";
import { Form, Formik, FormikProps } from "formik";
import OtpInput from "react-otp-input";

import { FormControl, Grid } from "@mui/material";
import FormField from "components/FormField";
import FaiButton from "MuiComponents/FaiButton";
import FaiInput from "MuiComponents/FaiInput";
import FaiSelect from "MuiComponents/FaiSelect";
import { Select } from "antd";
import countryDialCodes from "constants/countryCodes";
import Axios from "services/api/config";
import { setBasicInfo } from "services/api/routes/Auth";
import PageHeader from "../components/PageHeader";

interface InitialObject {
	firstName: string;
	lastName: string;
	email: string;
	designation: string;
	primaryContactNo: string;
}

function BasicDetails({ onNextStep = () => {} }) {
	const { palette, borders } = theme;
	const [initialObject] = useState<InitialObject>({
		firstName: "",
		lastName: "",
		email: "",
		designation: "",
		primaryContactNo: "",
	});
	const [isLoading, setLoading] = useState(false);

	const onContinue = () => {};

	const onSubmit = async (data: object) => {
		try {
			setLoading(true);
			const { data } = await Axios({
				...setBasicInfo,
				data: {},
			});
			console.log("data", data);
			// You can trigger the next step here if necessary
			onNextStep();
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<FaiBox className="p-6 rounded-b-lg" bgColor={palette.white.main}>
				<FaiBox className={"coupon-form-content"}>
					<PageHeader
						title="Basic Details"
						content="Please provide the details of the primary contact person for your
							company. This person will be the point of contact between your
							company and Fashion AI."
					/>
					<Formik
						initialValues={initialObject}
						enableReinitialize={true}
						validateOnBlur={true}
						validateOnChange={true}
						// Updated onSubmit to handle the event
						onSubmit={(values, actions) => {
							console.log("Form Submitted with values: ", values);
							onSubmit(values);

							// Reset the form or perform any other actions like API calls
							actions.setSubmitting(false); // reset submitting state
						}}
					>
						{(props: FormikProps<InitialObject>) => {
							const {
								handleSubmit,
								// setFieldValue,
								handleChange,
								handleBlur,
								values,
								touched,
								// isValid,
								errors,
							} = props;
							return (
								<Form onSubmit={handleSubmit} className="py-4">
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"First Name"}
													name={"firstName"}
													placeholder={"Enter First Name"}
													value={values.firstName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.firstName && Boolean(errors.firstName)}
													helperText={touched.firstName && errors.firstName}
													autoFocus
													isRequired
												/>
											</FaiBox>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Last Name"}
													name={"lastName"}
													placeholder={"Enter Last Name"}
													value={values.lastName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.lastName && Boolean(errors.lastName)}
													helperText={touched.lastName && errors.lastName}
													isRequired
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Email Address"}
													name={"email"}
													placeholder={"Enter Email"}
													value={values.email}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.email && Boolean(errors.email)}
													helperText={touched.email && errors.email}
													disabled
													isRequired
												/>
											</FaiBox>
										</Grid>

										<Grid
											item
											xs={12}
											sm={6}
											style={{ display: "flex", alignItems: "center" }}
										>
											<FaiBox
												sx={{
													display: "flex",
													flexDirection: "column",
													width: "100%",
												}}
											>
												<FaiBox mb={0.5} display="inline-block">
													<FaiTypography
														component="label"
														className="!text-base leading-6 !font-medium"
														variant="caption"
														fontWeight="bold"
														textTransform="capitalize"
														isRequired
													>
														Contact number
													</FaiTypography>
												</FaiBox>

												<FaiBox sx={{ display: "flex", alignItems: "center" }}>
													<FormControl sx={{ minWidth: 90 }}>
														{/* <Select
															labelId="country-code-label"
															id="country-code-select"
															value={91}
															onChange={() => { }}
															displayEmpty
															// options={[
															// 	{ value: "91", label: "+91" },
															// 	{ value: "92", label: "+92" },
															// ]}
															isRequired
														/> */}
														<Select
															showSearch
															style={{ height: 40 }}
															placeholder="Search to Select"
															optionFilterProp="label"
															filterSort={(optionA, optionB) =>
																(optionA?.label ?? "")
																	.toLowerCase()
																	.localeCompare(
																		(optionB?.label ?? "").toLowerCase()
																	)
															}
															rootClassName=""
															options={countryDialCodes}
														/>
													</FormControl>

													<FaiInput
														placeholder="9876543210"
														fullWidth
														name="primaryContactNo"
														value={values.primaryContactNo}
														onChange={handleChange}
														onBlur={handleBlur}
														error={
															touched.primaryContactNo &&
															Boolean(errors.primaryContactNo)
														}
														helperText={
															touched.primaryContactNo &&
															errors.primaryContactNo
														}
														sx={{
															marginLeft: 1,
															"& .MuiOutlinedInput-root": {
																"& fieldset": {
																	borderLeft: "none", // Remove border between select and input
																},
															},
														}}
													/>
												</FaiBox>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox mb={0.5} display="inline-block">
												<FaiTypography
													component="label"
													className="!text-base leading-6 !font-medium"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
													isRequired
												>
													Designation
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Select Designation"}
												name={"designation"}
												options={[
													{ value: "desg1", label: "Desg 1" },
													{ value: "desg2", label: "Desg 2" },
												]}
												isRequired
											/>
										</Grid>
									</Grid>

									<FaiBox pt={"15px"}>
										<Grid container justifyContent="flex-end">
											<FaiButton
												type="submit"
												color="primary"
												variant={"contained"}
												onClick={onContinue}
												disabled={isLoading}
												className="!rounded-lg"
											>
												Continue
											</FaiButton>
										</Grid>
									</FaiBox>
								</Form>
							);
						}}
					</Formik>
				</FaiBox>
			</FaiBox>
			<FaiBox
				bgColor={palette.white.main}
				className={"coupon-form-content p-6"}
				my={"15px"}
				borderRadius={borders.borderRadius.md}
			>
				<FaiBox className="mb-6">
					<FaiTypography variant="h4" className="!text-xl !font-semibold !mb-2">
						Verify the Contact Number
					</FaiTypography>
					<FaiTypography variant="h6" className="!text-base !font-normal">
						Please enter the One Time Password (OTP) that has been sent to your
						phone no. 984323434.
					</FaiTypography>
				</FaiBox>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<FaiBox xs={12} sm={6}>
							{/* Label */}
							<FaiBox mb={1} display="inline-block">
								<FaiTypography
									component="label"
									className="!text-base !font-medium"
									variant="caption"
									fontWeight="bold"
									textTransform="capitalize"
									isRequired
								>
									Enter OTP
								</FaiTypography>
							</FaiBox>

							{/* OTP Input */}
							<OtpInput
								value={""}
								onChange={() => {}}
								numInputs={4}
								// renderSeparator={<span>-</span>}
								renderInput={(props) => <input {...props} />}
								inputStyle={{
									width: "40px",
									height: "40px",
									gap: "4px",
									fontSize: "16px",
									borderRadius: "8px",
									border: "1px solid #ccc",
									textAlign: "center",
								}}
								containerStyle="gap-4"
							/>
						</FaiBox>
					</Grid>{" "}
				</Grid>
				<FaiBox pt={"15px"}>
					<Grid container justifyContent="flex-end" className="flex gap-5">
						<FaiButton color="primary" variant="contained">
							Verify Number
						</FaiButton>
						<FaiButton variant="outlined">Change Number</FaiButton>
					</Grid>
				</FaiBox>
			</FaiBox>
		</>
	);
}
export default BasicDetails;
